import Fade from '@material-ui/core/Fade';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import { makeStyles } from '@material-ui/core/styles';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import LicenceData from '../../components/_Licence/licenceData';
import licenceInfo from '../../constant/licenceInfo';
import { licenceText } from '../../constant/licenceText';
import { theme } from '../../utils/muiTheme';

// const GreenSwitch = withStyles({
//   switchBase: {
//     color: theme.palette.thirdBackgroundColor,
//     '&$checked': {
//       color: theme.palette.secundaryColor,
//     },
//     '&$checked + $track': {
//       backgroundColor: theme.palette.secundaryColor,
//     },
//   },
//   checked: {},
//   track: {
//     backgroundColor: theme.palette.fourthBackgroundColor,
//   },
// })(Switch);

const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: theme.palette.seventhBackgroundColor,
    color: theme.palette.primaryColor,
    padding: '24px',
    borderRadius: '15px',
    height: '91vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    opacity: 1,
    transition: 'opacity 0.5s ease-in-out',
  },
  rootFading: {
    opacity: 0,
  },
  header: {
    fontSize: '1.3rem',
    fontWeight: 'bold',
    marginBottom: '0.8rem',
    color: theme.palette.thirdColor,
  },
  description: {
    color: theme.palette.primaryColor,
    marginBottom: '2rem',
    fontSize: '0.85rem',
    lineHeight: '1.4',
    maxWidth: '1100px',
  },
  tabsContainer: {
    backgroundColor: 'transparent',
    marginBottom: '24px',
    boxShadow: 'none',
    width: '100%',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  tabs: {
    backgroundColor: 'transparent',
    marginBottom: '24px',
    borderBottom: 'none',
    width: '100%',
    '& .MuiTabs-flexContainer': {
      borderBottom: `1px solid ${theme.palette.thirdBackgroundColor}`,
      display: 'flex',
      justifyContent: 'space-between',
    },
    '& .MuiTab-root': {
      color: 'rgba(255, 255, 255, 0.5)',
      textTransform: 'none',
      fontSize: '0.9rem',
      flex: 1,
      padding: '6px 16px',
      '&.Mui-selected': {
        color: theme.palette.primaryColor,
      },
    },
    '& .MuiTabs-indicator': {
      backgroundColor: theme.palette.thirdColor,
      height: '2px',
    },
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  licencesContainer: {
    overflowY: 'auto',
    flexGrow: 1,
    marginBottom: '20px',
    paddingRight: '30px',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: theme.palette.thirdBackgroundColor,
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.fourthBackgroundColor,
      borderRadius: '4px',
      '&:hover': {
        background: theme.palette.eighthBackgroundColor,
      },
    },
  },
  acceptTerms: {
    color: theme.palette.primaryColor,
    marginTop: 'auto',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    border: `1px solid ${theme.palette.thirdBackgroundColor}`,
    borderRadius: '4px',
    marginBottom: '10px',
  },
  acceptText: {
    fontSize: '0.85rem',
    color: theme.palette.primaryColor,
  },
  infoIcon: {
    color: theme.palette.secundaryColor,
    fontSize: '1.2rem',
    marginLeft: '10px',
  },
}));

const LicencesView = () => {
  const [focusedLicence, setFocusedLicence] = useState(licenceInfo[0].title);
  const [tabValue, setTabValue] = useState(0);
  // const [acceptTerms, setAcceptTerms] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);
  const classes = useStyles();

  useEffect(() => {
    setTimeout(() => {
      setFadeIn(true);
    }, 100);
  }, []);

  const licencesRefs = useMemo(() => {
    const refs = {};
    licenceInfo.forEach(licence => {
      refs[licence.title] = React.createRef();
    });
    return refs;
  }, []);

  const handleTabChange = useCallback(
    (_, newValue) => {
      setTabValue(newValue);
      const newLicenceTitle = licenceInfo[newValue].title;
      setFocusedLicence(newLicenceTitle);

      if (
        licencesRefs[newLicenceTitle] &&
        licencesRefs[newLicenceTitle].current
      ) {
        licencesRefs[newLicenceTitle].current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    },
    [licencesRefs]
  );

  const handleFocusChange = useCallback(
    title => {
      setFocusedLicence(title);

      const tabIndex = licenceInfo.findIndex(
        license => license.title === title
      );
      if (tabIndex !== -1) {
        setTabValue(tabIndex);
      }

      if (licencesRefs[title] && licencesRefs[title].current) {
        licencesRefs[title].current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    },
    [licencesRefs]
  );

  // const handleSwitchChange = useCallback(event => {
  //   setAcceptTerms(event.target.checked);
  // }, []);

  const tabs = useMemo(
    () => (
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        className={classes.tabs}
        variant="fullWidth"
        indicatorColor="primary"
        aria-label="Pestañas de tipos de licencia"
      >
        {licenceInfo.map((licence, index) => (
          <Tab key={index} label={licence.title} />
        ))}
      </Tabs>
    ),
    [tabValue, handleTabChange, classes.tabs]
  );

  const licencesContent = useMemo(
    () =>
      licenceInfo.map((licence, index) => (
        <LicenceData
          key={index}
          title={licence.title}
          subtitle={licence.subtitle}
          content={licence.content}
          isFocused={focusedLicence === licence.title}
          onFocusChange={handleFocusChange}
          ref={licencesRefs[licence.title]}
        />
      )),
    [focusedLicence, handleFocusChange, licencesRefs]
  );

  // ! Se deja comentadada la funcionalidad de aceptar terminos y condiciones por si se utilizamas adelante
  // const termsSwitch = useMemo(
  //   () => (
  //     <FormControlLabelComponent
  //       control={
  //         <GreenSwitch
  //           checked={acceptTerms}
  //           onChange={handleSwitchChange}
  //           name="acceptTerms"
  //         />
  //       }
  //       label={
  //         <span className={classes.acceptText}>
  //           {licenceText.acceptTermsText}
  //         </span>
  //       }
  //       labelPlacement="start"
  //       tooltip={
  //         <TooltipMessage
  //           title={licenceText.tooltipTitle}
  //           message={licenceText.tooltipMessage}
  //           href={null}
  //         />
  //       }
  //     />
  //   ),
  //   [acceptTerms, handleSwitchChange, classes.acceptText]
  // );

  return (
    <Fade in={fadeIn} timeout={500}>
      <div className={`${classes.root} ${!fadeIn ? classes.rootFading : ''}`}>
        <h1 className={classes.header}>{licenceText.header}</h1>
        <p className={classes.description}>{licenceText.description}</p>

        <div className={classes.tabsContainer}>{tabs}</div>

        <div className={classes.content}>
          <div className={classes.licencesContainer}>{licencesContent}</div>
          {/* <div className={classes.acceptTerms}>{termsSwitch}</div> */}
        </div>
      </div>
    </Fade>
  );
};

export default LicencesView;
